// Section 17 shortage dialog view models (24.14): pure functions of the paused game (and the
// player's edits), memoised on their inputs. The live strip and the per-row hints come from the
// real phases through projectShortage (24.8), never from a second implementation.
import {
  catalog,
  projectShortage,
  type Config,
  type GameState,
  type HouseholdId,
  type JobId,
  type PersonId,
  type Projection,
  type PromptAnswer,
  type RationPolicyId,
  type RationResource,
  type Season,
  type ShortagePrompt,
} from '../sim/api';

/** Section 17 row column "seasons short in the last four". */
export const RECENT_SEASONS = 4;

export type ShortageAnswer = Extract<PromptAnswer, { kind: 'shortage' }>;

export interface ShortageRow {
  id: PersonId;
  name: string;
  ageYears: number;
  job: JobId;
  strength: number;
  health: number;
  seasonsShort: number;
  householdId: HouseholdId;
  /** The household's eldest member, to name it ("Ada's household"). */
  householdName: string;
  demand: number;
  /** The policy's proposed share of need, 0..1. */
  proposed: number;
  /** A standing override for this resource, if any. */
  standingPin: number | null;
}

export interface ShortageVM {
  promptId: string;
  resource: RationResource;
  season: Season;
  year: number;
  covered: number;
  population: number;
  needed: number;
  available: number;
  shortfall: number;
  floor: number;
  policy: RationPolicyId;
  seedAvailable: number;
  /** Sorted by the proposal: most fed first, then the policy's order. */
  rows: ShortageRow[];
  trustNow: number;
  farmerIds: PersonId[];
  farmerStrengthNow: number | null;
}

function shortagePrompt(game: GameState): ShortagePrompt | null {
  if (game.phaseCursor === null) return null;
  const prompt = game.pendingPrompts[0];
  return prompt?.kind === 'shortage' ? prompt : null;
}

const mean = (xs: readonly number[]): number | null =>
  xs.length === 0 ? null : xs.reduce((a, b) => a + b, 0) / xs.length;

function build(game: GameState, prompt: ShortagePrompt): ShortageVM {
  const { seasonsPerYear, shortThreshold } = catalog();
  const recent = game.history.slice(-RECENT_SEASONS);
  const rank = new Map(prompt.order.map((id, i) => [id, i]));
  const pins = game.rationOverrides[prompt.resource];
  const rows: ShortageRow[] = [];
  for (const id of prompt.order) {
    const p = game.people[id];
    if (p === undefined) continue;
    const household = game.households[p.householdId];
    const eldest = (household?.memberIds ?? [p.id])
      .map((m) => game.people[m])
      .filter((m) => m !== undefined)
      .sort((a, b) => b.ageSeasons - a.ageSeasons || (a.id < b.id ? -1 : 1))[0];
    const pin =
      prompt.resource === 'warmth'
        ? pins.households[p.householdId]
        : (pins.people[p.id] ?? pins.households[p.householdId]);
    rows.push({
      id,
      name: p.name,
      ageYears: Math.floor(p.ageSeasons / seasonsPerYear),
      job: p.job,
      strength: p.strength,
      health: p.health,
      seasonsShort: recent.filter(
        (r) =>
          (r.needs[id]?.[prompt.resource === 'medicine' ? 'healthCare' : prompt.resource] ?? 1) <
          shortThreshold,
      ).length,
      householdId: p.householdId,
      householdName: eldest?.name ?? p.name,
      demand: prompt.demand[id] ?? 0,
      proposed: prompt.proposed[id] ?? 1,
      standingPin: pin ?? null,
    });
  }
  rows.sort((a, b) => b.proposed - a.proposed || (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0));
  const people = Object.values(game.people);
  const farmers = people.filter((p) => p.job === 'farmer').map((p) => p.id);
  return {
    promptId: prompt.id,
    resource: prompt.resource,
    season: game.season,
    year: game.year,
    covered: prompt.covered,
    population: prompt.population,
    needed: prompt.needed,
    available: prompt.available,
    shortfall: prompt.shortfall,
    floor: prompt.floor,
    policy: prompt.policy,
    seedAvailable: prompt.seedAvailable,
    rows,
    trustNow: mean(people.map((p) => p.trust)) ?? 0,
    farmerIds: farmers.sort(),
    farmerStrengthNow: mean(farmers.map((id) => game.people[id]?.strength ?? 0)),
  };
}

let lastVM: { game: GameState; vm: ShortageVM | null } | null = null;

/** The dialog's rows and header, or null unless the season is paused on a shortage. */
export function shortageVM(game: GameState | null): ShortageVM | null {
  if (game === null) return null;
  if (lastVM?.game === game) return lastVM.vm;
  const prompt = shortagePrompt(game);
  const vm = prompt === null ? null : build(game, prompt);
  lastVM = { game, vm };
  return vm;
}

/** The player's edits in the dialog. */
export interface ShortageEdits {
  /** Rows the player set (share of need); the policy refills everyone else. */
  shares: Record<PersonId, number>;
  /** Rows whose setting becomes a standing override. */
  pinned: PersonId[];
  eatSeed: number;
  saveAsOrder: string | null;
}

export const noEdits: ShortageEdits = { shares: {}, pinned: [], eatSeed: 0, saveAsOrder: null };

export function hasEdits(edits: ShortageEdits): boolean {
  return (
    Object.keys(edits.shares).length > 0 ||
    edits.pinned.length > 0 ||
    edits.eatSeed > 0 ||
    edits.saveAsOrder !== null
  );
}

export function shortageAnswer(vm: ShortageVM, edits: ShortageEdits): ShortageAnswer {
  const pins: Record<PersonId, number> = {};
  for (const id of edits.pinned) {
    const row = vm.rows.find((r) => r.id === id);
    if (row !== undefined) pins[id] = edits.shares[id] ?? row.proposed;
  }
  const name = edits.saveAsOrder?.trim() ?? '';
  return {
    kind: 'shortage',
    shares: { ...edits.shares },
    eatSeed: Math.min(vm.seedAvailable, Math.max(0, edits.eatSeed)),
    saveAsOrder: name === '' ? null : name,
    ...(edits.pinned.length > 0 ? { pins } : {}),
  };
}

/** "Apply equal shares": everyone at stock / demand (with any seed the player chose to eat). */
export function equalShares(vm: ShortageVM, eatSeed = 0): Record<PersonId, number> {
  const share = vm.needed > 0 ? Math.min(1, (vm.available + eatSeed) / vm.needed) : 1;
  const out: Record<PersonId, number> = {};
  for (const row of vm.rows) out[row.id] = share;
  return out;
}

/** Section 17 live projection strip. */
export interface ShortageStrip {
  /** Food on hand at the end of next season (stock plus seed). */
  foodNextSeason: number | null;
  foodProducedNextSeason: number | null;
  farmerStrength: number | null;
  farmerStrengthNow: number | null;
  expectedDeaths: number;
  trustChange: number;
  /** Gini of food received relative to need, 0..1. */
  inequality: number;
  /** What each person is allotted this season under the answer (before household sharing). */
  allotted: Record<PersonId, number>;
}

function stripOf(vm: ShortageVM, projection: Projection): ShortageStrip {
  const now = projection.seasons[0];
  const next = projection.seasons[1];
  const allotted: Record<PersonId, number> = {};
  const rations = now?.rations?.[vm.resource];
  for (const row of vm.rows) {
    const need = vm.resource === 'medicine' ? 'healthCare' : vm.resource;
    allotted[row.id] = rations?.[row.id] ?? now?.needs[row.id]?.[need] ?? row.proposed;
  }
  return {
    foodNextSeason: next === undefined ? null : next.stocks.food + next.seedReserved,
    foodProducedNextSeason: next === undefined ? null : next.flows.food.production,
    farmerStrength:
      now === undefined ? null : mean(vm.farmerIds.map((id) => now.strength[id] ?? 0)),
    farmerStrengthNow: vm.farmerStrengthNow,
    expectedDeaths: now?.expectedDeaths ?? 0,
    trustChange: (now?.trust ?? vm.trustNow) - vm.trustNow,
    inequality: now?.gini ?? 0,
    allotted,
  };
}

const stripCache = new Map<string, ShortageStrip>();
let stripGame: GameState | null = null;

/** Project the paused season with the player's answer. Memoised per game and answer. */
export function shortageStrip(
  game: GameState,
  answer: ShortageAnswer,
  cfg?: Config,
): ShortageStrip | null {
  const vm = shortageVM(game);
  if (vm === null) return null;
  if (stripGame !== game) {
    stripCache.clear();
    stripGame = game;
  }
  const key = JSON.stringify(answer);
  const hit = stripCache.get(key);
  if (hit !== undefined) return hit;
  const strip = stripOf(vm, projectShortage(game, answer, 2, cfg === undefined ? {} : { cfg }));
  stripCache.set(key, strip);
  return strip;
}

/** Section 17 one-line consequence hint per row. */
export interface ShortageHint {
  /** Already short last season: another short season starts health falling. */
  secondSeason: boolean;
  strengthNow: number;
  strengthIfNone: number | null;
  healthNow: number;
  healthIfNone: number | null;
  job: JobId;
  /** Change in this person's job output next season if given nothing, vs the proposal (-0.4 = -40%). */
  outputChange: number | null;
}

let hintCache: { game: GameState; hints: Record<PersonId, ShortageHint> } | null = null;

/**
 * Hints from two projections: everyone given nothing, and the proposal. A person's strength and
 * output depend on their own ration, so one "nothing" projection answers every row at once.
 */
export function shortageHints(game: GameState, cfg?: Config): Record<PersonId, ShortageHint> {
  if (hintCache?.game === game) return hintCache.hints;
  const vm = shortageVM(game);
  const hints: Record<PersonId, ShortageHint> = {};
  if (vm !== null) {
    const opts = cfg === undefined ? {} : { cfg };
    const none: Record<PersonId, number> = {};
    for (const row of vm.rows) none[row.id] = 0;
    const starve = projectShortage(game, { ...shortageAnswer(vm, noEdits), shares: none }, 2, opts);
    const base = projectShortage(game, shortageAnswer(vm, noEdits), 2, opts);
    const streakKey = {
      food: 'hungry',
      water: 'thirsty',
      warmth: 'cold',
      medicine: 'hungry',
    } as const;
    for (const row of vm.rows) {
      const p = game.people[row.id];
      if (p === undefined) continue;
      const outNone = starve.seasons[1]?.personOutputs?.[row.id];
      const outBase = base.seasons[1]?.personOutputs?.[row.id];
      hints[row.id] = {
        secondSeason: p.streaks[streakKey[vm.resource]] >= 1,
        strengthNow: p.strength,
        strengthIfNone: starve.seasons[0]?.strength[row.id] ?? null,
        healthNow: p.health,
        healthIfNone: starve.seasons[0]?.health?.[row.id] ?? null,
        job: p.job,
        outputChange:
          outNone !== undefined && outBase !== undefined && outBase > 0
            ? outNone / outBase - 1
            : null,
      };
    }
  }
  hintCache = { game, hints };
  return hints;
}
